import dayjs from 'dayjs';
import {catchError, combineLatest, map, Observable} from 'rxjs';

import {ApiErrorModel, isApiErrorObject} from '@auth/data-access';
import {
	CycleTagType,
	EmptyObjectResponse,
	IAddLogUserCommentRequest,
	ICancelRequest,
	ICreateCycleRequest,
	ICreateCycleResponse,
	ICycle,
	IDuplicateCycleRequest,
	IDuplicateCycleResponse,
	IEditCycle,
	IField,
	IGetCycleExResponse,
	IGetCycleResponse,
	IGetReportRequest,
	IGetReportResponse,
	IMetaTagValue,
	IResumeCycleRequest,
	IStartCycleRequest,
	ISubmitCycleRequest,
	ISubmitCycleResponse,
	IUpdateCycleRequest,
	IUpdateCycleResponse,
	TriggerActionType,
} from '@core/api/data-access';
import {StoredFile} from '@global-data';
import {deepCopy} from '@utils';

import {cycleCreatorPlaceholder} from '../constants';
import {CycleOperationsCyclesService} from './cycle.operations.cycles.service';
import {CycleApiErrors} from './cycles.error-handler';
import {CyclesService} from './cycles.service';
import isCycleTriggerError = CycleApiErrors.ErrorTypes.isCycleTriggerError;

export class CycleCyclesService {
	public readonly Operations: CycleOperationsCyclesService = new CycleOperationsCyclesService(this.cyclesService);

	constructor(private cyclesService: CyclesService) {}

	/* -------------------------------------------------- Get -------------------------------------------------- */
	public get(cycleId: string): Observable<IGetCycleResponse> {
		return this.cyclesService.apiService.get<IGetCycleResponse>(this.cyclesService.getUrl(`cycle`), {id: cycleId});
	}

	/*	------------------------------------------------- Extended ------------------------------------------------- */
	public getExtended(cycleId: string): Observable<IGetCycleExResponse> {
		return this.cyclesService.apiService.get<IGetCycleExResponse>(this.cyclesService.getUrl(`cycleex`), {id: cycleId});
	}

	/* ------------------------- Get cycle and document ------------------------- */
	public getCycleAndDocument(
		cycleId: string,
		initLibs = false,
		flatBase64 = false,
		extendedCycle = true,
		bidi = false,
	): Observable<{
		cycle: IGetCycleResponse;
		document: StoredFile;
	}> {
		const getCycleObs = this.get(cycleId);
		const getDocumentObs = this.cyclesService.Document.getDocument(cycleId, flatBase64);
		return combineLatest([getCycleObs, getDocumentObs]).pipe(
			map(([cycle, document]) => {
				return {cycle, document};
			}),
		);
	}

	/* --------------------------------------------- Create (draft) --------------------------------------------- */
	public create(cycle: IEditCycle, message?: string): Observable<ICreateCycleResponse> {
		const createRequest: ICreateCycleRequest = {cycle: {...cycle, id: ''}, message};
		return this.cyclesService.apiService.post<ICreateCycleResponse, ICreateCycleRequest>(this.cyclesService.getUrl('cycle'), createRequest);
	}

	/* ------------------------------------------------- Update ------------------------------------------------- */
	public update(cycle: IEditCycle, message?: string): Observable<IUpdateCycleResponse> {
		const updateRequest: IUpdateCycleRequest = {cycle: deepCopy(cycle), message};
		return this.cyclesService.apiService.put<IUpdateCycleResponse, IUpdateCycleRequest>(this.cyclesService.getUrl('cycle'), updateRequest);
	}

	/* -------------------------------------------------  Start ------------------------------------------------- */
	public start(cycleId: string, message?: string, cycle?: ICycle): Observable<string> {
		const request: IStartCycleRequest = {id: cycleId, message};
		return this.cyclesService.apiService.post<EmptyObjectResponse, EmptyObjectResponse>(this.cyclesService.getUrl('start'), {}, {}, request).pipe(
			catchError(err => {
				if (!cycle || !isApiErrorObject(err)) throw err;
				if (isCycleTriggerError(err)) {
					// check if the trigger error reason is missing email for creator && cycleCreatorPlaceholder is set as trigger action recipient:
					if (cycle.triggers.some(t => t.action.type === TriggerActionType.Email && t.action['recipient'] === cycleCreatorPlaceholder)) {
						// if (!this.cyclesService.store.selectSignal(selectActiveUser)()?.email) {
						// 	err.userMessage = 'pages.cycles.error.missing-trigger-action-email';
						// }
						if (!this.cyclesService.activeUserStore.user$()?.email) {
							err.userMessage = 'pages.cycles.error.missing-trigger-action-email';
						}
					}
					return ApiErrorModel.showToast(err, this.cyclesService.injector);
				}
				throw err;
			}),
			map(() => {
				return cycleId;
			}),
		);
	}

	/* ------------------------------------------------- Submit ------------------------------------------------- */
	public submit(request: ISubmitCycleRequest): Observable<ISubmitCycleResponse> {
		return this.cyclesService.apiService.post<EmptyObjectResponse, ISubmitCycleRequest>(this.cyclesService.getUrl('submit'), request);
	}

	/* ------------------------------------------------- Delete ------------------------------------------------- */
	public delete(cycleId: string): Observable<EmptyObjectResponse> {
		return this.cyclesService.apiService.delete<EmptyObjectResponse>(this.cyclesService.getUrl('cycle'), {id: cycleId});
	}

	/* ------------------------------------------------- Search ------------------------------------------------- */
	public search(search: string): Observable<ICycle> {
		return this.cyclesService.apiService.get<{cycle: ICycle}>(this.cyclesService.getUrl('search'), {search: search}).pipe(
			map(response => {
				return response.cycle;
			}),
		);
	}

	/* ------------------------------------------------- Duplicate ------------------------------------------------- */
	public duplicate(cycleId: string, newName: string, document: string, fields: IField[] = [], metaTags: IMetaTagValue[] = [], tags: CycleTagType[] = []): Observable<ICycle> {
		const request: IDuplicateCycleRequest = {
			cycleId,
			documentData: document,
			cycleName: newName,
			fields,
			metaTags,
			tags,
		};
		return this.cyclesService.apiService.post<IDuplicateCycleResponse, IDuplicateCycleRequest>(this.cyclesService.getUrl('duplicate'), request).pipe(
			map(response => {
				return response.cycle;
			}),
		);
	}

	/* ------------------------------------------------- Archive ------------------------------------------------- */
	public archive(cycleId: string): Observable<EmptyObjectResponse> {
		const requestParam: {
			id: string;
		} = {
			id: cycleId,
		};
		return this.cyclesService.apiService.post<EmptyObjectResponse, object>(this.cyclesService.getUrl('archive'), {}, {}, requestParam);
	}

	/* ------------------------------------------------- Cancel ------------------------------------------------- */
	public cancel(cycleId: string, message: string): Observable<EmptyObjectResponse> {
		const request: ICancelRequest = {
			cycleId,
			message,
		};
		return this.cyclesService.apiService.post<EmptyObjectResponse, object>(this.cyclesService.getUrl('cancel'), request);
	}

	/* ------------------------------------------------- Resume ------------------------------------------------- */
	public resume(cycleId: string, message: string): Observable<EmptyObjectResponse> {
		const request: IResumeCycleRequest = {
			cycleId,
			message,
		};
		return this.cyclesService.apiService.post<EmptyObjectResponse, IResumeCycleRequest>(this.cyclesService.getUrl('resume'), request);
	}

	/* --------------------------------- Notify --------------------------------- */
	public notify(cycleId: string, alternate = ''): Observable<EmptyObjectResponse> {
		if (alternate === '') {
			const requestParam: {
				id: string;
			} = {
				id: cycleId,
			};
			return this.cyclesService.apiService.post<EmptyObjectResponse, object>(this.cyclesService.getUrl('notify'), {}, {}, requestParam);
		} else {
			const requestParam: {
				id: string;
				alternate: string;
			} = {
				id: cycleId,
				alternate,
			};
			return this.cyclesService.apiService.post<EmptyObjectResponse, object>(this.cyclesService.getUrl('notify'), {}, {}, requestParam);
		}
	}

	/* ------------------------------------------------- Get Company Cycle Reports ------------------------------------------------- */
	public getCompanyReports(from: Date, to: Date): Observable<IGetReportResponse> {
		const params: IGetReportRequest = {
			from: dayjs(from).startOf('day').format('YYYY-MM-DDTHH:mm:ssZ'),
			to: dayjs(to).endOf('day').format('YYYY-MM-DDTHH:mm:ssZ'),
		};

		return this.cyclesService.apiService.get<IGetReportResponse>(this.cyclesService.getUrl('report'), {...params});
	}

	public addCommentToLog(cycleId: string, message: string, system: boolean): Observable<EmptyObjectResponse> {
		const body: IAddLogUserCommentRequest = {
			cycleId,
			message: message,
			system,
		};
		return this.cyclesService.apiService.post<EmptyObjectResponse, object>(this.cyclesService.getUrl('log'), body);
	}
}
